/**
 * Phase 6: adventures. An Adventure is a reusable template (typed in, built by Grok from an idea, or one of the
 * pre-built one-shots). A campaign plays its own copy, so editing the template never changes a game in progress.
 */
// ---------------------------------------------------------------- cleaning (used for typed, edited and AI input)
const str = (v, max) => (typeof v === "string" ? v.trim().slice(0, max) : typeof v === "number" ? String(v) : "");
const list = (v, max, f) => (Array.isArray(v) ? v : []).slice(0, max).map(f).filter((x) => x !== null);
const obj = (x) => (x && typeof x === "object" && !Array.isArray(x) ? x : {});
const lvl = (v, d) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) ? Math.min(20, Math.max(1, n)) : d;
};
const DIFFS = ["easy", "medium", "hard", "deadly"];
export const ADVENTURE_LIMITS = { acts: 8, scenes: 8, beats: 6, npcs: 20, locations: 20, encounters: 24, loot: 24, secrets: 12, wrapUp: 10 };
/** Normalize anything (a form, an import, Grok's JSON) into valid content. Never throws. */
export function cleanAdventureContent(input) {
    const o = obj(input);
    const levelMin = lvl(o.levelMin, 1);
    const levelMax = Math.max(levelMin, lvl(o.levelMax, levelMin));
    const acts = list(o.acts, ADVENTURE_LIMITS.acts, (x) => {
        const a = obj(x);
        const title = str(a.title, 120);
        if (!title)
            return null;
        return {
            title, summary: str(a.summary, 3000), goal: str(a.goal, 500), scenes: list(a.scenes, ADVENTURE_LIMITS.scenes, (s) => str(s, 600) || null),
            beats: list(a.beats, ADVENTURE_LIMITS.beats, (s) => str(s, 300) || null),
        };
    });
    return {
        pitch: str(o.pitch, 1200),
        overview: str(o.overview, 4000),
        levelMin,
        levelMax,
        length: str(o.length, 60) || "One-shot (3–4 hours)",
        tone: str(o.tone, 120),
        acts,
        npcs: list(o.npcs, ADVENTURE_LIMITS.npcs, (x) => {
            const n = obj(x);
            const name = str(n.name, 80);
            return name ? { name, role: str(n.role, 120), description: str(n.description, 1200), secret: str(n.secret, 800), voiceHint: str(n.voiceHint, 200) } : null;
        }),
        locations: list(o.locations, ADVENTURE_LIMITS.locations, (x) => {
            const n = obj(x);
            const name = str(n.name, 80);
            return name ? { name, description: str(n.description, 1200), secret: str(n.secret, 800) } : null;
        }),
        encounters: list(o.encounters, ADVENTURE_LIMITS.encounters, (x) => {
            const e = obj(x);
            const name = str(e.name, 120);
            if (!name)
                return null;
            const d = str(e.difficulty, 10).toLowerCase();
            const act = Math.round(Number(e.act));
            return {
                name, act: Number.isFinite(act) ? Math.min(acts.length || 1, Math.max(0, act)) : 0, monsters: str(e.monsters, 600),
                difficulty: DIFFS.includes(d) ? d : "medium", tactics: str(e.tactics, 800),
            };
        }),
        loot: list(o.loot, ADVENTURE_LIMITS.loot, (x) => {
            const l = obj(x);
            const name = str(l.name, 120);
            return name ? { name, where: str(l.where, 300), notes: str(l.notes, 600) } : null;
        }),
        secrets: list(o.secrets, ADVENTURE_LIMITS.secrets, (s) => str(s, 600) || null),
        // Kept aligned with secrets: blank entries stay so route N still belongs to secret N.
        revealRoutes: (Array.isArray(o.revealRoutes) ? o.revealRoutes : []).slice(0, ADVENTURE_LIMITS.secrets).map((s) => str(s, 600)),
        wrapUp: list(o.wrapUp, ADVENTURE_LIMITS.wrapUp, (s) => str(s, 300) || null),
        ending: str(o.ending, 2000),
    };
}
/** Problems that stop an adventure from being played (empty = fine). */
export function adventureProblems(title, c) {
    const out = [];
    if (!title.trim())
        out.push("Give it a title.");
    if (!c.acts.length)
        out.push("Add at least one act.");
    return out;
}
/** The DM's view of the current act (for the DM context). */
export function dmAdventure(title, c, currentAct, beatsDone = []) {
    if (!c.acts.length)
        return null;
    const i = Math.min(Math.max(0, currentAct), c.acts.length - 1);
    const last = i === c.acts.length - 1;
    return {
        title,
        overview: c.overview,
        actNumber: i + 1,
        totalActs: c.acts.length,
        act: c.acts[i],
        nextActTitles: c.acts.slice(i + 1).map((a) => a.title),
        npcs: c.npcs,
        locations: c.locations,
        encounters: c.encounters.filter((e) => e.act === 0 || e.act === i + 1),
        loot: c.loot,
        secrets: c.secrets,
        revealRoutes: c.revealRoutes ?? [],
        beatsDone,
        ...(last ? { wrapUp: c.wrapUp ?? [] } : {}),
        ending: last ? c.ending : null,
    };
}
/** Which key beats of an act are done, from campaign_adventures.beats_done ("act:beat"). */
export function beatsDoneFor(done, act) {
    return done.map((x) => x.split(":").map(Number)).filter(([a, b]) => a === act && Number.isInteger(b)).map(([, b]) => b);
}
/** JSON shape Grok must return when building an adventure. */
export const ADVENTURE_JSON_SPEC = `Return ONE JSON object:
{
  "title": string,
  "pitch": string (2-4 sentences read to the players; no spoilers),
  "overview": string (DM-only: what is really going on, the villain's plan, how the pieces connect),
  "levelMin": integer, "levelMax": integer, "length": string, "tone": string,
  "acts": [ { "title": string, "summary": string (DM view, 3-6 sentences), "goal": string, "scenes": [string] (2-4 short scene ideas), "beats": [string] (2-4 key beats that must happen before the act can end, e.g. "The party learns about the pact", each reachable in more than one way) } ] (3-5 acts),
  "npcs": [ { "name": string, "role": string, "description": string (look, manner, what they want), "secret": string, "voiceHint": string (e.g. "old man, gravelly, kind") } ] (4-8),
  "locations": [ { "name": string, "description": string (sensory, 2-3 sentences), "secret": string } ] (3-6),
  "encounters": [ { "name": string, "act": integer (1-based), "monsters": string (SRD 5e creatures with counts, balanced for the party), "difficulty": "easy"|"medium"|"hard"|"deadly", "tactics": string } ] (3-6, including at least one social or puzzle challenge),
  "loot": [ { "name": string, "where": string, "notes": string } ] (3-8, level-appropriate, SRD items or simple homebrew),
  "secrets": [string] (2-4 twists or clues),
  "revealRoutes": [string] (same order as secrets: 2-3 ways the players can find each one out, e.g. "Brannoc's records (Investigation DC 12); Pike if befriended"),
  "wrapUp": [string] (end-of-adventure checklist: rewards to pay, loot to hand out, the villain's fate, hooks),
  "ending": string (how it can end, including if the party fails)
}`;
//# sourceMappingURL=adventure.js.map